/**
 * THE KITCHEN CHIT'S LINES (roundtable 2026-10-15 T10, T14, A1 — D172) — pure,
 * so the two LIVE chit renderers print the same paper from one rule: the
 * device chit (`modules/printing/coordinatorArtifact.tsx`, QZ) and the browser
 * fallback (`modules/kitchen/components/KitchenChit.tsx`).
 *
 * T10: a store setting, default on, prints identical plates of ONE ticket as
 * one line with the count first ("3×"). Identical = same dish, same variant and
 * extra picks, same swaps, same combo contents, same note. Never across tickets
 * (so never across rounds). A VOID, CHANGE or REMAKE line is never grouped: a
 * correction is about one plate, and the head chef's condition for grouping
 * at all was that a void states what remains («ملغى 1 من 3 — المتبقي 2»).
 */

/** What a cook reads about one line — the grouping identity. */
export type ChitLineFacts = {
  dish: string;
  variants: readonly string[];
  addons: readonly string[];
  swaps: readonly string[];
  combo: readonly string[];
  note: string | null;
  /** False for VOID / CHANGE / REMAKE lines: those print one per plate. */
  groupable: boolean;
};

export type ChitLineGroup<T> = {
  key: string;
  /** How many plates this line stands for; 1 for an ungrouped line. */
  count: number;
  /** The first plate of the group — what the line prints. */
  line: T;
  lines: T[];
};

/** The identity two plates must share to print as one line. */
export function chitLineKey(facts: ChitLineFacts): string {
  const sorted = (values: readonly string[]) => [...values].sort();
  return JSON.stringify([
    facts.dish,
    sorted(facts.variants),
    sorted(facts.addons),
    sorted(facts.swaps),
    sorted(facts.combo),
    (facts.note ?? "").trim(),
  ]);
}

/**
 * The chit's lines in first-appearance order. With `grouped` off — or for a
 * line that may not group — every plate is its own line, exactly as before.
 */
export function groupChitLines<T>(
  lines: readonly T[],
  grouped: boolean,
  factsOf: (line: T) => ChitLineFacts,
): ChitLineGroup<T>[] {
  const result: ChitLineGroup<T>[] = [];
  const byKey = new Map<string, ChitLineGroup<T>>();
  lines.forEach((line, index) => {
    const facts = factsOf(line);
    if (!grouped || !facts.groupable) {
      result.push({ key: `line:${index}`, count: 1, line, lines: [line] });
      return;
    }
    const key = chitLineKey(facts);
    const group = byKey.get(key);
    if (group) {
      group.count += 1;
      group.lines.push(line);
      return;
    }
    const fresh: ChitLineGroup<T> = { key, count: 1, line, lines: [line] };
    byKey.set(key, fresh);
    result.push(fresh);
  });
  return result;
}

/**
 * A VOID line's remainder, from the snapshot stored on it (D172): how many of
 * the group are now cancelled, of how many, and what is left. Null when the
 * line carries no snapshot (a void stored before D172, or a pre-print void).
 */
export function voidRemainder(line: {
  groupTotal?: number | null;
  groupRemaining?: number | null;
}): { voided: number; total: number; remaining: number } | null {
  const total = line.groupTotal;
  const remaining = line.groupRemaining;
  if (typeof total !== "number" || typeof remaining !== "number" || total <= 0) {
    return null;
  }
  return { voided: Math.max(total - remaining, 0), total, remaining };
}

/**
 * «إضافة {n}» (T14): n = round − 1, and nothing at all on round 1 — the first
 * fire of a bill is not an addition to anything.
 */
export function chitAdditionNumber(round: number | null | undefined): number | null {
  return typeof round === "number" && round > 1 ? round - 1 : null;
}

/**
 * `HH:MM`, 24-hour, Western digits, in the STORE's zone (A1) — the print
 * computer's own clock zone is not the restaurant's. An unknown zone falls
 * back to the device's.
 */
export function chitClock(iso: string, timezone?: string | null): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  const options: Intl.DateTimeFormatOptions = {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  };
  try {
    return new Intl.DateTimeFormat("en-GB", {
      ...options,
      ...(timezone ? { timeZone: timezone } : {}),
    }).format(date);
  } catch {
    return new Intl.DateTimeFormat("en-GB", options).format(date);
  }
}
